import { getPlatformName, PlatformIconComponent } from "@/biz/platform";
import { message, Popover, Tooltip } from "antd";
import { useState } from "react";
import { CopyIcon, SameInfluencerIcon, StarBgIcon } from "../icons";
import copyToClipboard from "@/utils/copyToClipboard";
import { getOrderChannels, normalizeOrderPlatform } from "@/utils/orderV2";
import { formatNumber } from "@/biz/tool";
import { useTranslation } from "react-i18next";
import { Tag } from "aha-components";
import { AdsCampaignMatchingAccuracy } from "@/pages/CreateCampaign/shared/config";
import { isFeishuEnv } from "@/utils/feishu";
import BenchmarkTags from "@/pages/CreateCampaign/components/SettingsProfiles/BenchmarkTags";
import CreatorAvatarFrame, {
  resolveCreatorAvatarFrameType,
} from "@/components/CreatorAvatarFrame";

export default function TableCreatorName({
  handleClick,
  item,
  showFollowers = false,
  listShowFollowers = false,
  showCreatorPersonaType = true,
  showUserName = true,
}: {
  handleClick?: Function;
  item: any;
  showFollowers?: boolean;
  listShowFollowers?: boolean;
  showCreatorPersonaType?: boolean;
  showUserName?: boolean;
}) {
  const { t } = useTranslation();
  const [isNameHovered, setIsNameHovered] = useState(false);
  const userName = item?.userName?.trim().replace(/^@/, "").trim();
  const creatorNameMaxWidthClass = isFeishuEnv()
    ? "max-w-[120px]"
    : "max-w-[140px] md:max-w-[160px]";
  const creatorAvatarFrameType = resolveCreatorAvatarFrameType(item);
  const channels = getOrderChannels(item);
  const isMultiChannelOrder = channels.length > 1;
  const isMultiChannelParent = listShowFollowers && isMultiChannelOrder && !item?.__channelRowType;
  const isChannelRow = listShowFollowers && item?.__channelRowType === "channel";
  const showAccountLink = showUserName && !!userName && !isChannelRow && !isMultiChannelParent;
  const displayName = isChannelRow
    ? (userName ? `@${userName}` : undefined)
    : item?.creatorName;

  const canCopyName = listShowFollowers && !isChannelRow && !!item?.creatorName;

  // 将 matchingAccuracy 枚举值转换为本地化文本
  const getMatchingAccuracyLabel = (accuracy: string) => {
    const matchText = t("components.tableCreatorName.match", {
      defaultValue: "match",
    });
    if (accuracy === AdsCampaignMatchingAccuracy.FOCUS_MODE) {
      return (
        t("campaignCreate.settings.matchingAccuracy.focusMode.label", {
          defaultValue: "Focus mode",
        }) +
        " " +
        matchText
      );
    }
    if (accuracy === AdsCampaignMatchingAccuracy.SCALE_MODE) {
      return (
        t("campaignCreate.settings.matchingAccuracy.scaleMode.label", {
          defaultValue: "Scale mode",
        }) +
        " " +
        matchText
      );
    }
    if (accuracy === AdsCampaignMatchingAccuracy.MAX_SCALE_MODE) {
      return (
        t("campaignCreate.settings.matchingAccuracy.maxScaleMode.label", {
          defaultValue: "Max scale mode",
        }) +
        " " +
        matchText
      );
    }
    return accuracy;
  };

  return (
    <div
      className="flex items-center"
      onClick={() => handleClick && handleClick(item)}
    >
      <div className={`cursor-pointer ${isChannelRow ? "mr-2 shrink-0" : "mr-3"}`}>
        <div
          className={`${isChannelRow ? "w-10 h-10" : listShowFollowers ? "w-14 h-14" : "w-12 h-12"} justify-start items-center gap-3 flex relative flex-shrink-0`}
        >
          <img
            alt=""
            data-channel-avatar={isMultiChannelParent ? "" : undefined}
            className={`${isChannelRow ? "w-10 h-10" : listShowFollowers ? "w-14 h-14" : "w-12 h-12"} rounded-full flex-shrink-0`}
            src={item?.creatorProfilePictureLink || item?.avatarUrl}
          />
          {!isMultiChannelOrder && !isChannelRow && (
            <div className="absolute right-0 bottom-0">
              <PlatformIconComponent
                platform={item?.platform}
                className="w-[20px] h-[20px]"
              />
            </div>
          )}
          {creatorAvatarFrameType && !isChannelRow && (
            <div
              className={`${listShowFollowers ? "w-20 h-20 top-[-12px] left-[-12px]" : "w-[72px] h-[72px] top-[-12px] left-[-12px]"} absolute z-[-1] rounded-full`}
            >
              <CreatorAvatarFrame type={creatorAvatarFrameType} size={56} />
            </div>
          )}
        </div>
      </div>
      <div className={`flex flex-col ${isChannelRow ? "min-w-0 gap-0" : "gap-1"}`}>
        <div className="flex items-center gap-1">
          <div
            className={`group/creator-name text-zinc-950 ${isChannelRow ? "font-normal" : "font-semibold"} cursor-pointer line-clamp-1 flex items-center gap-1`}
            onMouseLeave={() => setIsNameHovered(false)}
          >
            {displayName && <Tooltip title={displayName}>
              {isChannelRow && item?.creatorProfileLink ? (
                <a
                  className={`${creatorNameMaxWidthClass} overflow-hidden text-ellipsis whitespace-nowrap !text-[#2563EB]`}
                  href={item?.creatorProfileLink}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  {displayName}
                </a>
              ) : (
                <div
                  className={`${creatorNameMaxWidthClass} overflow-hidden text-ellipsis whitespace-nowrap ${isChannelRow ? "text-[#2563EB]" : ""}`}
                  onMouseEnter={() => canCopyName && setIsNameHovered(true)}
                >
                  {displayName}
                </div>
              )}
            </Tooltip>}
            <div className="grid shrink-0 items-center justify-items-start empty:hidden">
              {canCopyName && (
                <Tooltip title={t("components.tableCreatorName.copyUserName")}>
                  <button
                    type="button"
                    aria-label={t("components.tableCreatorName.copyUserName")}
                    className={`col-start-1 row-start-1 flex items-center justify-center gap-0.5 rounded-md border border-[#E5E5E5] bg-[#FAFAFA] py-px pl-1.5 pr-2 font-['Inter'] text-xs font-medium leading-[18px] text-[#404040] focus-visible:opacity-100 focus-visible:pointer-events-auto ${isNameHovered ? "opacity-100" : "opacity-0 pointer-events-none"}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (copyToClipboard(item.creatorName)) {
                        message.success(t("common.messages.copySuccess"));
                      } else {
                        message.error(t("common.messages.copyFail"));
                      }
                    }}
                  >
                    <CopyIcon className="h-3 w-3 text-[#737373]" />
                    <span>{t("common.buttons.copy")}</span>
                  </button>
                </Tooltip>
              )}
              {item?.creatorMixedScore && item?.creatorDeliveryCount >= 4 && (
                <div className={`col-start-1 row-start-1 border border-[#FFD1A9] bg-[#FFF6ED] w-[47px] h-[20px] flex items-center justify-center rounded-md gap-1 ${canCopyName ? "group-has-[:focus-visible]/creator-name:invisible" : ""} ${canCopyName && isNameHovered ? "invisible" : ""}`}>
                  <StarBgIcon className="text-primary w-[14px] h-[14px]" />
                  <div className="justify-center text-primary text-[12px] font-semibold font-['DM_Sans']">
                    {item?.creatorMixedScore}
                  </div>
                </div>
              )}
            </div>
          </div>
          {item?.creatorReferenceFrom && (
            <Tooltip
              color="#000"
              title={
                <div>
                  {t(
                    "components.tableCreatorName.influencerMatchedFromReference",
                  )}{" "}
                  <a
                    className="text-link"
                    href={item?.creatorReferenceFrom}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {item?.creatorReferenceFrom}
                  </a>
                </div>
              }
            >
              <span>
                <SameInfluencerIcon className="text-[#009966] flex-shrink-0" />
              </span>
            </Tooltip>
          )}
        </div>
        <div
          className={`flex items-center ${isChannelRow ? "gap-1 text-xs leading-[18px] font-normal font-['DM_Sans'] tracking-[-0.12px]" : isMultiChannelParent ? "gap-1" : listShowFollowers ? "gap-2" : "gap-6"}`}
        >
          {isMultiChannelParent && (
            <Popover
              trigger="hover"
              placement="top"
              arrow={false}
              overlayInnerStyle={{ padding: 8, borderRadius: 8 }}
              content={
                <div className="flex flex-col gap-2" onClick={(e) => e.stopPropagation()}>
                  {channels.map((channel) => {
                    const platform = normalizeOrderPlatform(channel.platform) || channel.platform;
                    const name = channel.userName?.trim().replace(/^@/, "").trim();
                    const label = name ? `@${name}` : channel.displayName || getPlatformName(platform);
                    return (
                      <div key={channel.channelKey} className="flex items-center gap-1 py-0.5 text-xs leading-[18px]">
                        <PlatformIconComponent platform={platform} className="h-4 w-4 shrink-0" />
                        {channel.profileLink ? (
                          <a
                            className="!text-[#2563EB] whitespace-nowrap"
                            href={channel.profileLink}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {label}
                          </a>
                        ) : (
                          <span className="whitespace-nowrap">{label}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              }
            >
              <div className="flex shrink-0 items-center -space-x-1" onClick={(e) => e.stopPropagation()}>
                {channels.map((channel) => (
                  <span key={channel.channelKey} className="relative flex h-4 w-4 items-center justify-center overflow-hidden rounded-full bg-white">
                    <PlatformIconComponent
                      platform={normalizeOrderPlatform(channel.platform) || channel.platform}
                      className="h-full w-full"
                    />
                  </span>
                ))}
              </div>
            </Popover>
          )}
          {isChannelRow && (
            <div className="flex items-center gap-1 whitespace-nowrap text-xs">
              <PlatformIconComponent
                platform={item?.platform}
                className="w-4 h-4 flex-shrink-0"
              />
              <span>{getPlatformName(item?.platform)}</span>
            </div>
          )}
          {showAccountLink && (
            <Tooltip title={`@${userName}`}>
              <a
                className={`${listShowFollowers ? "!max-w-[80px] min-w-0 truncate" : "max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap flex-shrink-0"} creator-list-table-content-link inline-block text-xs font-medium !text-[#2B7FFF]`}
                href={item?.creatorProfileLink}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                @{userName}
              </a>
            </Tooltip>
          )}
          {showFollowers && (
            <>
              {item?.followers && (
                <div className="inline-flex justify-start items-center gap-1">
                  <div className="justify-start text-Zinc-950 text-xs font-medium font-['DM_Sans'] leading-none">
                    {formatNumber(item?.followers)}
                  </div>
                  <div className="justify-start text-font&icon-secondary text-xs font-normal font-['DM_Sans'] leading-none">
                    {t("components.tableCreatorName.following")}
                  </div>
                </div>
              )}
              {item?.estimateView && (
                <div className="inline-flex justify-start items-center gap-1">
                  <div className="justify-start text-Zinc-950 text-xs font-medium font-['DM_Sans'] leading-none">
                    {formatNumber(item?.estimateView)}
                  </div>
                  <div className="justify-start text-font&icon-secondary text-xs font-normal font-['DM_Sans'] leading-none">
                    {t("components.tableCreatorName.estimatedViewsPost")}
                  </div>
                </div>
              )}
              {item?.matchingAccuracy && (
                <Tag
                  color={
                    item?.matchingAccuracy ===
                    AdsCampaignMatchingAccuracy.FOCUS_MODE
                      ? "#8E51FF"
                      : item?.matchingAccuracy ===
                          AdsCampaignMatchingAccuracy.SCALE_MODE
                        ? "#FE9A00"
                        : "#00B8DB"
                  }
                  variant="fill"
                  className="whitespace-nowrap"
                >
                  {getMatchingAccuracyLabel(item?.matchingAccuracy)}
                </Tag>
              )}
            </>
          )}
          {listShowFollowers && item?.followers ? (
            <>
              <div className={`w-[1px] ${isMultiChannelParent || isChannelRow ? "h-[10px]" : "h-[16px]"} bg-borderColor flex-shrink-0`}></div>
              <div className={isChannelRow ? "flex min-w-0 whitespace-nowrap" : "whitespace-nowrap flex-shrink-0"}>
                <span className={`text-[#79716B] ${isChannelRow ? "truncate" : ""}`}>
                  {t("components.tableCreatorName.followers")}
                </span>
                <span className={`text-[#1C1917] ${isChannelRow ? "shrink-0" : ""}`}>
                  {formatNumber(item?.followers)}
                </span>
              </div>
            </>
          ) : null}
        </div>
        {item?.creatorPersonaType && showCreatorPersonaType && (
          <Tooltip title={item.creatorPersonaType}>
            <div className="line-clamp-3">
              <span className="text-[#79716B]">
                {t("components.tableCreatorName.influencerProfile", {
                  defaultValue: "Influencer profile: ",
                })}
              </span>
              <span className="text-[#1C1917]">{item.creatorPersonaType}</span>
            </div>
          </Tooltip>
        )}
        <BenchmarkTags
          type="multiple"
          list={item?.competitorProducts || []}
        />
      </div>
    </div>
  );
}
